import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  Card, Modal, Btn, Input, Select, StatusBadge, InfoAlert,
  DetailRow, Table, Pagination,
} from '../components/UI';
import {
  IconWallet, IconEdit, IconCheck, IconBan, IconTrash,
  IconGift, IconDevice, IconUsers,
} from '../components/Icons';
import { fmt } from '../utils/helpers';

// ── Local icons ───────────────────────────────────────────────────────────────
const IconBack    = () => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IconMail    = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>;
const IconRefresh = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
const IconRevoke  = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
const IconExtend  = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>;
const IconActivity= () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

// Binary GB (÷1024³) to stay consistent with how the cron computes the cap.
function fmtGB(bytes) {
  return (Number(bytes || 0) / 1024 ** 3).toFixed(2);
}


// ── Parse meta whether it arrives as a string or object ────────────────────
function parseMeta(meta) {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try { return JSON.parse(meta); } catch { return {}; }
  }
  return meta;
}
// ── Per-subscription data usage bar ─────────────────────────────────────────────
// capMb = subscription.meta.data_limit_mb (0 / null = unlimited).
// usedBytes = sum of (download + upload) across all devices on the subscription.
function UsageBar({ usedBytes, capMb, compact = false }) {
  const cap = Number(capMb || 0);
  const isUnlimited = !cap || cap <= 0;
  const capBytes = cap * 1024 * 1024;            // binary MB → bytes (matches cron)
  const used = Number(usedBytes || 0);
  const pct = isUnlimited ? 0 : Math.min(100, (used / capBytes) * 100);
  const barColor = pct >= 90 ? 'var(--red)' : pct >= 70 ? '#f59e0b' : 'var(--green)';

  return (
    <div style={{ marginTop: compact ? 0 : 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        {!compact && (
          <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Data Usage
          </span>
        )}
        <span style={{ fontSize: 12, fontFamily: 'Geist Mono, monospace', fontWeight: 600, color: 'var(--text2)', marginLeft: compact ? 0 : 'auto' }}>
          {isUnlimited ? `${fmtGB(used)} GB used` : `${fmtGB(used)} of ${fmtGB(capBytes)} GB`}
        </span>
      </div>

      {isUnlimited ? (
        <div style={{ height: 8, borderRadius: 20, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em' }}>UNLIMITED</span>
        </div>
      ) : (
        <>
          <div style={{ height: 8, borderRadius: 20, background: 'var(--border2)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 20, transition: 'width 0.3s' }} />
          </div>
          {!compact && (
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              {pct >= 100 ? 'Cap reached' : `${fmtGB(Math.max(0, capBytes - used))} GB remaining`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Avatar({ name, size = 48 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--accent-dim)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: 'var(--accent)', flexShrink: 0,
    }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, fontFamily: 'Syne, sans-serif', color: 'var(--text)' }}>{children}</h3>
      {action}
    </div>
  );
}

function StatTile({ label, value, sub, color = 'var(--accent)' }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', flex: 1, minWidth: 100 }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'Syne, sans-serif', color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [user, setUser]     = useState(null);
  const [wallet, setWallet] = useState(null);
  const [subs, setSubs]     = useState([]);
  const [txns, setTxns]     = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [devices, setDevices] = useState([]);
  // Live connectivity per MAC, from the Omada controller via getUser. The
  // admin device list itself only carries DB rows, so it has no online state.
  const [deviceStatus, setDeviceStatus] = useState([]);
  const [usage, setUsage]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState({});
  const [busy, setBusy]     = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [plans, setPlans]   = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, wRes, sRes, txRes, dRes] = await Promise.allSettled([
        api.getUser(id),
        api.getUserWallet(id),
        api.getUserSubscriptions(id),
        api.getTransactions({ user_id: id, page: txPage, limit: 15 }),
        api.getDevices({ user_id: id, limit: 50 }),
      ]);
      if (uRes.status === 'fulfilled')  {
        setUser(uRes.value?.user || uRes.value);
        setDeviceStatus(uRes.value?.device_status || []);
      }
      if (wRes.status === 'fulfilled')  setWallet(wRes.value?.wallet || wRes.value);
      if (sRes.status === 'fulfilled')  setSubs(sRes.value?.subscriptions || []);
      if (txRes.status === 'fulfilled') { setTxns(txRes.value?.transactions || []); setTxTotal(txRes.value?.total || 0); }
      if (dRes.status === 'fulfilled')  {
        const devList = dRes.value?.devices || [];
        setDevices(devList);
        // Aggregate usage from devices / subscription_devices
        setUsage(devList);
      }
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, txPage]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const open  = (type, extra = {}) => {
    setForm(extra);
    setModal(type);
    if (type === 'grant') {
      setPlansLoading(true);
      api.getPlans()
        .then(r => {
          const fetched = r.plans || r || [];
          setPlans(fetched);
          // Pre-select first plan
          if (fetched.length > 0) setForm(f => ({ ...f, plan: fetched[0].id }));
        })
        .catch(() => {})
        .finally(() => setPlansLoading(false));
    }
  };
  const close = () => { setModal(null); setForm({}); };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast(msg, 'success'); close(); loadAll(); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (loading && !user) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ color: 'var(--text3)', fontSize: 14 }}>Loading user…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 16 }}>User not found.</div>
        <Btn variant="ghost" onClick={() => navigate('/users')}>← Back to Users</Btn>
      </div>
    );
  }


  const activeSub = subs.find(s => s.status === 'active');
  const totalSpend = txns.filter(t => t.type === 'debit' && t.status === 'success').reduce((a, t) => a + Number(t.amount), 0);
  const totalTopup = txns.filter(t => t.type === 'topup' && t.status === 'success').reduce((a, t) => a + Number(t.amount), 0);
  const totalDown  = usage.reduce((a, d) => a + Number(d.total_download_bytes || 0), 0);
  const totalUp    = usage.reduce((a, d) => a + Number(d.total_upload_bytes  || 0), 0);


  // ── Shared-pool usage per subscription (sum of all its devices' totals) ──────
  const usageBySub = devices.reduce((acc, d) => {
    const sid = d.subscription_id;
    if (!sid) return acc;
    acc[sid] = (acc[sid] || 0)
      + Number(d.total_download_bytes || 0)
      + Number(d.total_upload_bytes   || 0);
    return acc;
  }, {});

  const TABS = ['overview', 'subscriptions', 'transactions', 'devices'];

  // ── Transaction table columns
  const txCols = [
    { key: 'desc',   label: 'Description', render: t => <span style={{ fontSize: 13 }}>{t.description || '—'}</span> },
    { key: 'amount', label: 'Amount', render: t => (
      <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12, fontWeight: 600, color: t.type === 'topup' ? 'var(--green)' : 'var(--red)' }}>
        {t.type === 'topup' ? '+' : '−'}{fmt.naira(t.amount)}
      </span>
    )},
    { key: 'status', label: 'Status', render: t => <StatusBadge status={t.status} /> },
    { key: 'ref',    label: 'Reference', render: t => <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Geist Mono, monospace' }}>{t.reference || '—'}</span> },
    { key: 'date',   label: 'Date', render: t => <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmt.date(t.createdAt)}</span> },
  ];

  return (
    <div className="page-enter">
      {/* ── Back + Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/users')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border2)', borderRadius: 8, color: 'var(--text3)', cursor: 'pointer', padding: '7px 12px', fontSize: 12, fontFamily: 'inherit', flexShrink: 0, marginTop: 2 }}>
          <IconBack /> Users
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Avatar name={user.name} size={52} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: 'var(--text)' }}>{user.name}</h1>
              <StatusBadge status={user.is_active ? 'active' : 'inactive'} />
              {user.is_verified && <StatusBadge status="success" />}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
              {user.email} · Joined {fmt.date(user.createdAt)} · {user.lodge?.name || 'Lodge not detected'}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="subtle" icon={IconWallet} onClick={() => open('fund')}>Fund</Btn>
          <Btn size="sm" variant="subtle" icon={IconEdit}   onClick={() => open('adjust')}>Adjust</Btn>
          <Btn size="sm" variant="subtle" icon={IconGift}   onClick={() => open('grant')}>Grant Plan</Btn>
          <Btn size="sm" variant="subtle" icon={IconMail}   onClick={() => open('email')}>Email</Btn>
          <Btn size="sm" variant={user.is_active ? 'ghost' : 'subtle'}
            icon={user.is_active ? IconBan : IconCheck}
            style={{ color: user.is_active ? 'var(--red)' : 'var(--green)' }}
            onClick={() => run(() => api.toggleUserStatus(user.id, !user.is_active), user.is_active ? 'User disabled' : 'User enabled')}>
            {user.is_active ? 'Disable' : 'Enable'}
          </Btn>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatTile label="Balance"   value={fmt.naira(wallet?.balance)} color="var(--green)" />
        <StatTile label="Total Topup" value={fmt.naira(totalTopup)} />
        <StatTile label="Total Spend" value={fmt.naira(totalSpend)} color="var(--red)" />
        <StatTile label="Downloaded" value={fmtBytes(totalDown)} sub="all time" />
        <StatTile label="Uploaded"   value={fmtBytes(totalUp)}   sub="all time" />
        <StatTile label="Subscriptions" value={subs.length} sub={activeSub ? '1 active' : 'none active'} />
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              padding: '9px 16px', fontSize: 13, fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? 'var(--accent)' : 'var(--text3)',
              borderBottom: `2px solid ${activeTab === tab ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1, textTransform: 'capitalize', transition: 'all 0.12s',
            }}>
            {tab}
          </button>
        ))}
      </div>

      {/* ════════ OVERVIEW TAB ════════ */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

          {/* Profile info */}
          <Card>
            <SectionTitle>Profile</SectionTitle>
            <DetailRow label="Full Name"   value={user.name} />
            <DetailRow label="Email"       value={user.email} />
            <DetailRow label="Status"      value={<StatusBadge status={user.is_active ? 'active' : 'inactive'} />} />
            <DetailRow label="Verified"    value={<StatusBadge status={user.is_verified ? 'success' : 'pending'} />} />
            <DetailRow label="User ID"     value={<span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>{user.id}</span>} />
            <DetailRow label="Joined"      value={fmt.datetime(user.createdAt)} />
            {/* Lodge is derived from the access point the user connects through. */}
            <DetailRow label="Lodge"       value={user.lodge?.name || <span style={{ color: 'var(--text3)', fontSize: 12 }}>Not detected yet</span>} />
            {/* Per-account device cap. Blank = the global config applies. */}
            <DetailRow
              label="Device limit"
              value={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <select
                    value={user.device_limit ?? ''}
                    onChange={(e) => run(
                      () => api.setDeviceLimit(user.id, e.target.value === '' ? null : Number(e.target.value)),
                      'Device limit updated'
                    )}
                    style={{
                      background: 'var(--surface2)', color: 'var(--text)',
                      border: '1px solid var(--border2)', borderRadius: 7,
                      padding: '4px 8px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                    }}
                  >
                    <option value="">Default (global setting)</option>
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>{n} device{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </span>
              }
            />
          </Card>

          {/* Wallet info */}
          <Card>
            <SectionTitle
              children="Wallet"
              action={
                <button onClick={() => open('regenerateDVA')}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border2)', borderRadius: 7, color: 'var(--text3)', cursor: 'pointer', padding: '5px 10px', fontSize: 11, fontFamily: 'inherit' }}>
                  <IconRefresh /> Regenerate DVA
                </button>
              }
            />
            <DetailRow label="Balance"        value={<span style={{ fontFamily: 'Geist Mono, monospace', fontWeight: 700, color: 'var(--green)' }}>{fmt.naira(wallet?.balance)}</span>} />
            <DetailRow label="Account Number" value={wallet?.dva_account_number || <span style={{ color: 'var(--text3)', fontSize: 12 }}>Not generated</span>} />
            <DetailRow label="Bank"           value={wallet?.dva_bank_name       || '—'} />
            <DetailRow label="DVA Reference"  value={<span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>{wallet?.dva_reference || '—'}</span>} />
          </Card>

          {/* Active plan */}
          <Card>
            <SectionTitle children="Active Subscription" />
            {activeSub ? (
              <>
                <DetailRow label="Plan"     value={parseMeta(activeSub.meta).plan_name || activeSub.plan_details?.name || activeSub.plan} />
                <DetailRow label="Status"   value={<StatusBadge status={activeSub.status} />} />
                <DetailRow label="Price"    value={fmt.naira(activeSub.price)} />
                <DetailRow label="Expires"  value={fmt.date(activeSub.expires_at)} />
                <DetailRow label="Started"  value={fmt.date(activeSub.started_at)} />

                {/* ── Data usage (shared pool across all devices) ── */}
                <UsageBar
                  usedBytes={usageBySub[activeSub.id] || 0}
                  capMb={parseMeta(activeSub.meta).data_limit_mb}
                />

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Btn size="xs" variant="subtle" icon={IconExtend}
                    onClick={() => open('extend', { subId: activeSub.id, subName: parseMeta(activeSub.meta).plan_name || activeSub.plan })}>
                    Extend
                  </Btn>
                  <Btn size="xs" variant="ghost" style={{ color: 'var(--red)' }}
                    onClick={() => { if (window.confirm('Cancel this subscription?')) run(() => api.cancelSubscription(activeSub.id), 'Subscription cancelled'); }}>
                    Cancel Sub
                  </Btn>
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '12px 0' }}>No active subscription.</div>
            )}
          </Card>
        </div>
      )}

      {/* ════════ SUBSCRIPTIONS TAB ════════ */}
      {activeTab === 'subscriptions' && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px 0' }}>
            <SectionTitle children={`Subscriptions (${subs.length})`} />
          </div>
          {subs.length === 0 ? (
            <div style={{ padding: '40px 18px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No subscriptions yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Plan', 'Price', 'Status', 'Usage', 'Started', 'Expires', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '11px 14px', fontWeight: 600 }}>{parseMeta(s.meta).plan_name || s.plan_details?.name || s.plan}</td>
                      <td style={{ padding: '11px 14px', fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>{fmt.naira(s.price)}</td>
                      <td style={{ padding: '11px 14px' }}><StatusBadge status={s.status} /></td>
                      <td style={{ padding: '11px 14px', minWidth: 160 }}>
                        <UsageBar usedBytes={usageBySub[s.id] || 0} capMb={parseMeta(s.meta).data_limit_mb} compact />
                      </td>
                      <td style={{ padding: '11px 14px', color: 'var(--text3)', fontSize: 11, whiteSpace: 'nowrap' }}>{fmt.date(s.started_at)}</td>
                      <td style={{ padding: '11px 14px', color: 'var(--text3)', fontSize: 11, whiteSpace: 'nowrap' }}>{fmt.date(s.expires_at)}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {s.status === 'active' && (
                            <>
                              <Btn size="xs" variant="subtle" icon={IconExtend} onClick={() => open('extend', { subId: s.id, subName: parseMeta(s.meta).plan_name || s.plan })}>Extend</Btn>
                              <Btn size="xs" variant="ghost" style={{ color: 'var(--red)' }}
                                onClick={() => { if (window.confirm('Cancel?')) run(() => api.cancelSubscription(s.id), 'Cancelled'); }}>
                                Cancel
                              </Btn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ════════ TRANSACTIONS TAB ════════ */}
      {activeTab === 'transactions' && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px 0' }}>
            <SectionTitle children={`Transactions (${txTotal})`} />
          </div>
          <Table columns={txCols} rows={txns} loading={loading} emptyText="No transactions." />
          <div style={{ borderTop: '1px solid var(--border)', padding: '0 14px' }}>
            <Pagination page={txPage} total={txTotal} limit={15} onChange={setTxPage} />
          </div>
        </Card>
      )}

      {/* ════════ DEVICES TAB ════════ */}
      {activeTab === 'devices' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {devices.length === 0 ? (
            <Card><div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text3)', fontSize: 13 }}>No devices found.</div></Card>
          ) : devices.map(d => {
            const live = deviceStatus.find(s => s.mac_address === d.mac_address) || {};
            return (
            <Card key={d.id || d.mac_address}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                    <IconDevice />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{d.nickname || d.mac_address}</div>
                    <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>{d.mac_address}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                    <span style={{ color: 'var(--text3)' }}>↓ {fmtBytes(d.total_download_bytes)}</span>
                    <span style={{ color: 'var(--text3)' }}>↑ {fmtBytes(d.total_upload_bytes)}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: live.is_online ? 'rgba(34,197,94,0.12)' : 'rgba(100,100,100,0.1)',
                      color: live.is_online ? '#22c55e' : 'var(--text3)',
                    }}>{live.is_online ? 'ONLINE' : 'OFFLINE'}</span>
                    {live.ip_address && (
                      <span style={{ color: 'var(--text3)', fontFamily: 'Geist Mono, monospace' }}>{live.ip_address}</span>
                    )}
                    {live.ap_name && (
                      <span style={{ color: 'var(--text3)' }}>{live.ap_name}</span>
                    )}
                  </div>
                  <Btn size="xs" variant="ghost" icon={IconRevoke} style={{ color: 'var(--red)' }}
                    onClick={() => { if (window.confirm(`Revoke ${d.mac_address}?\n\nThis cuts its access AND removes it from the plan. The user would need to add it again.`)) run(() => api.revokeDevice(d.mac_address), 'Device revoked and removed from plan'); }}>
                    Revoke
                  </Btn>
                </div>
              </div>
              {/* subscription_devices has no first_seen/last_seen columns, so
                  those fields were always undefined and this block never
                  rendered. createdAt is when the device joined the plan; live
                  connectivity comes from `live` above. */}
              {d.createdAt && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 20, fontSize: 11, color: 'var(--text3)' }}>
                  <span>Added to plan: {fmt.datetime(d.createdAt)}</span>
                  {live.ap_name && <span>Currently on: {live.ap_name}</span>}
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}

      {/* ════════ MODALS ════════ */}

      {/* Fund Wallet */}
      {modal === 'fund' && (
        <Modal title={`Fund Wallet — ${user.name}`} onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <InfoAlert type="info">Current balance: <b>{fmt.naira(wallet?.balance)}</b></InfoAlert>
            <Input label="Amount (₦)" type="number" value={form.amount || ''} onChange={v => setForm(f => ({ ...f, amount: v }))} required />
            <Input label="Bank Reference" value={form.reference || ''} onChange={v => setForm(f => ({ ...f, reference: v }))} placeholder="TRF202506120001" hint="Reference from customer's bank transfer." required />
            <Input label="Description (optional)" value={form.description || ''} onChange={v => setForm(f => ({ ...f, description: v }))} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="success" icon={IconWallet} loading={busy} disabled={!form.amount || !form.reference}
                onClick={() => run(() => api.fundWallet(user.id, { amount: parseFloat(form.amount), reference: form.reference, description: form.description }), 'Wallet funded')}>
                Fund Wallet
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Adjust Wallet */}
      {modal === 'adjust' && (
        <Modal title={`Adjust Wallet — ${user.name}`} onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <InfoAlert type="info">Current balance: <b>{fmt.naira(wallet?.balance)}</b></InfoAlert>
            <Select label="Type" value={form.adjustType || 'topup'} onChange={v => setForm(f => ({ ...f, adjustType: v }))}
              options={[{ value: 'topup', label: '+ Credit (add funds)' }, { value: 'debit', label: '− Debit (remove funds)' }]} />
            <Input label="Amount (₦)" type="number" value={form.amount || ''} onChange={v => setForm(f => ({ ...f, amount: v }))} required />
            <Input label="Reason" value={form.description || ''} onChange={v => setForm(f => ({ ...f, description: v }))} required />
            <InfoAlert type={form.adjustType === 'debit' ? 'danger' : 'success'}>
              {form.adjustType === 'debit' ? '⚠ Will deduct from wallet.' : '✓ Will add to wallet.'}
            </InfoAlert>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant={form.adjustType === 'debit' ? 'danger' : 'success'} loading={busy} disabled={!form.amount || !form.description}
                onClick={() => run(() => api.adjustWallet(user.id, { amount: parseFloat(form.amount), type: form.adjustType || 'topup', description: form.description }), 'Wallet adjusted')}>
                Apply
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Grant Plan */}
      {modal === 'grant' && (
        <Modal title={`Grant Plan — ${user.name}`} onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {plansLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontSize: 13 }}>Loading plans…</div>
            ) : plans.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--red)', fontSize: 13 }}>No plans available. Please create a plan first.</div>
            ) : (
              <>
                <Select label="Plan" value={form.plan || plans[0]?.id} onChange={v => setForm(f => ({ ...f, plan: v }))}
                  options={plans.map(p => ({ value: p.id, label: `${p.name} — ${fmt.naira(p.price)}` }))} />
                <Input label="Device MAC Address" value={form.mac || ''} onChange={v => setForm(f => ({ ...f, mac: v }))} placeholder="xx:xx:xx:xx:xx:xx" hint="MAC address of the device to authorize." required />
                <Select label="Billing" value={form.grant_free || 'false'} onChange={v => setForm(f => ({ ...f, grant_free: v }))}
                  options={[{ value: 'false', label: 'Deduct from wallet' }, { value: 'true', label: 'Free grant — no charge' }]} />
                <InfoAlert type={form.grant_free === 'true' ? 'warning' : 'info'}>
                  {form.grant_free === 'true'
                    ? '⚠ No charge will be applied.'
                    : `💳 ${fmt.naira(plans.find(p => p.id === (form.plan || plans[0]?.id))?.price)} will be deducted.`}
                </InfoAlert>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn variant="ghost" onClick={close}>Cancel</Btn>
                  <Btn variant="success" icon={IconGift} loading={busy} disabled={!form.mac}
                    onClick={() => run(() => api.grantSubscription(user.id, { plan: form.plan || plans[0]?.id, mac_address: form.mac, grant_free: form.grant_free === 'true' }), 'Plan granted')}>
                    Grant Plan
                  </Btn>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Extend Subscription */}
      {modal === 'extend' && (
        <Modal title={`Extend — ${form.subName}`} onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <InfoAlert type="info">Enter the number of days to extend this subscription.</InfoAlert>
            <Input label="Days to Extend" type="number" min="1" value={form.days || ''} onChange={v => setForm(f => ({ ...f, days: v }))} required />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="success" icon={IconExtend} loading={busy} disabled={!form.days || form.days < 1}
                onClick={() => run(() => api.extendSubscription(form.subId, parseInt(form.days, 10)), `Subscription extended by ${form.days} day(s)`)}>
                Extend
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Send Email */}
      {modal === 'email' && (
        <Modal title={`Send Email — ${user.name}`} onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <InfoAlert type="info">Email will be sent to <b>{user.email}</b></InfoAlert>
            <Input label="Subject" value={form.subject || ''} onChange={v => setForm(f => ({ ...f, subject: v }))} placeholder="Important notice about your account" required />
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Message</label>
              <textarea
                value={form.body || ''}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your message here…"
                rows={6}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)',
                  color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
                  resize: 'vertical', boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="success" icon={IconMail} loading={busy} disabled={!form.subject || !form.body}
                onClick={() => run(() => api.sendEmail({ to: user.email, subject: form.subject, message: form.body }), 'Email sent')}>
                Send Email
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Regenerate DVA */}
      {modal === 'regenerateDVA' && (
        <Modal title="Regenerate Virtual Account" onClose={close}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <InfoAlert type="warning">
              This will create a new virtual account for <b>{user.name}</b>. Only do this if the existing account has errors or was never generated.
            </InfoAlert>
            {wallet?.dva_account_number && (
              <InfoAlert type="info">
                Current account: <b>{wallet.dva_account_number}</b> ({wallet.dva_bank_name})
              </InfoAlert>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={close}>Cancel</Btn>
              <Btn variant="danger" icon={IconRefresh} loading={busy}
                onClick={() => run(() => api.regenerateDVA(user.id), 'Virtual account regenerated')}>
                Regenerate
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}